import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppChrome } from '@/components/AppChrome';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { useFiling, useFilingActions } from './state';

export function FactsPage() {
  const access = useSurfaceAccess(SURFACES['filing-facts']);
  const filingId = useFiling((snapshot) => snapshot.context.filingId);
  const error = useFiling((snapshot) => snapshot.context.error);
  const saving = useFiling((snapshot) => snapshot.matches({ workflow: 'entering' }));
  const actions = useFilingActions();
  const [distributions, setDistributions] = useState('');
  const [fiduciaryFees, setFiduciaryFees] = useState('');
  const [mustDistributeIncome, setMustDistributeIncome] = useState(false);
  if (access === 'waiting') return <p className="p-8 text-sm text-muted-foreground">Checking screen access…</p>;
  if (access === 'unavailable')
    return <p className="p-8 text-sm text-destructive">This screen is unavailable right now.</p>;
  if (access === 'denied')
    return <p className="p-8 text-sm text-muted-foreground">You have no access to this screen.</p>;
  if (!filingId) return <NoFiling />;

  const dispatch = () => {
    const parsedDistributions = Number(distributions);
    const parsedFees = Number(fiduciaryFees);
    if (!Number.isFinite(parsedDistributions) || !Number.isFinite(parsedFees)) return;
    actions.enter({ filingId, distributions: parsedDistributions, fiduciaryFees: parsedFees, mustDistributeIncome });
  };
  return (
    <AppChrome
      title="Trust facts"
      description="Record the distribution duties and fiduciary fees used for calculation."
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Facts for selected Filing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">Recording facts for the selected Filing.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="distributions">Distributions</Label>
              <Input
                id="distributions"
                data-jr-key="enter-filing-facts-distributions"
                inputMode="decimal"
                value={distributions}
                onChange={(event) => setDistributions(event.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="fiduciary-fees">Fiduciary fees</Label>
              <Input
                id="fiduciary-fees"
                data-jr-key="enter-filing-facts-fiduciaryFees"
                inputMode="decimal"
                value={fiduciaryFees}
                onChange={(event) => setFiduciaryFees(event.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <Checkbox
              data-jr-key="enter-filing-facts-mustDistributeIncome"
              checked={mustDistributeIncome}
              onCheckedChange={(checked) => setMustDistributeIncome(checked === true)}
            />
            Must distribute income
          </label>
          {error && (
            <p className="text-sm text-destructive">The Filing facts could not be recorded. Please try again.</p>
          )}
          <Button
            data-jr-key="enter-filing-facts"
            onClick={dispatch}
            disabled={saving || distributions === '' || fiduciaryFees === ''}
          >
            {saving ? 'Recording…' : 'Record facts'}
          </Button>
        </CardContent>
      </Card>
    </AppChrome>
  );
}

function NoFiling() {
  return (
    <AppChrome title="Trust facts" description="A Filing must be open before facts can be recorded.">
      <Card>
        <CardContent className="space-y-3 p-6">
          <p className="font-medium">No Filing is open.</p>
          <p className="text-sm text-muted-foreground">Select a Client and Rule Package first.</p>
          <Button render={<Link to="/filing-setup" />}>Go to Filing setup</Button>
        </CardContent>
      </Card>
    </AppChrome>
  );
}
